import type { CreateFieldTaskBody } from '../api/types';
import type { DeliveryBlockDraft } from './delivery-form';
import { blokPengirimanDari, externalRefUntukTugas, type SuratJalanErp } from './tarik-do';

/**
 * Menyusun badan `POST /api/field-tasks` dari isian layar "Tugas baru".
 *
 * Diekstrak dari `TaskNewPage.submit` (#1655) tanpa mengubah perilakunya. YANG DIUJI PERILAKU
 * adalah fungsi ini dan `kirimTugasBaru` (`badan-tugas.spec.ts`): hasil `externalRefUntukTugas`
 * benar-benar masuk ke argumen `createTask`.
 *
 * YANG TIDAK DIUJI PERILAKU: layar itu sendiri. Workforce-web tidak punya runner komponen, jadi
 * bahwa `TaskNewPage` memang memanggil `kirimTugasBaru` (dan bukan menyusun badan sendiri atau
 * menimpa isinya) hanya dijaga regex tekstual di `workforce-api/.../external-ref-erp.spec.ts`.
 */
export function susunBadanTugas(args: {
  readonly suratTerpilih: SuratJalanErp | null;
  readonly assetId: string | null | undefined;
  readonly taskTypeCode: string;
  readonly title: string;
  readonly priority: string;
  readonly destinationName: string;
  readonly destinationAddress: string;
  readonly instructions: string;
  readonly isDelivery: boolean;
  readonly delivery: DeliveryBlockDraft;
}): CreateFieldTaskBody {
  const { suratTerpilih, delivery } = args;
  return {
    externalRef: externalRefUntukTugas({ suratTerpilih, assetId: args.assetId }),
    taskTypeCode: args.taskTypeCode,
    title: args.title,
    priority: args.priority,
    // Objek bersarang — server menolak field datar dengan 400 (forbidNonWhitelisted).
    destination:
      args.destinationName.trim() || args.destinationAddress.trim()
        ? {
            name: args.destinationName.trim() || null,
            address: args.destinationAddress.trim() || null,
          }
        : null,
    instructions: args.instructions.trim() || null,
    delivery: args.isDelivery
      ? suratTerpilih
        ? blokPengirimanDari(suratTerpilih)
        : {
            deliveryOrderNumber: delivery.deliveryOrderNumber.trim() || null,
            customerName: delivery.customerName.trim() || null,
            contactPerson: delivery.contactPerson.trim() || null,
            contactPhone: delivery.contactPhone.trim() || null,
            pickup:
              delivery.pickupLocationName.trim() || delivery.pickupAddress.trim()
                ? {
                    name: delivery.pickupLocationName.trim() || null,
                    address: delivery.pickupAddress.trim() || null,
                  }
                : null,
            deliveryNotes: delivery.deliveryNotes.trim() || null,
            items: delivery.items.map((item) => ({
              lineNo: item.lineNo,
              itemName: item.itemName.trim(),
              quantity: item.quantity.trim(),
              sku: item.sku.trim() || null,
              unit: item.unit.trim() || null,
            })),
          }
      : null,
  };
}

/**
 * Langkah yang memanggil `createTask` dengan badan hasil `susunBadanTugas` — satu-satunya jalan
 * badan itu sampai ke server. `buat` diinjeksi (layar mengoper `createTask` asli; uji mengoper
 * stub) sehingga argumen yang benar-benar diterima pembuatan tugas dapat diperiksa tanpa runner
 * komponen. Tidak ada perilaku tambahan: badan dioper apa adanya.
 */
export function kirimTugasBaru<A, R>(
  buat: (auth: A, kunci: string, badan: CreateFieldTaskBody) => Promise<R>,
  auth: A,
  kunci: string,
  isian: Parameters<typeof susunBadanTugas>[0],
): Promise<R> {
  return buat(auth, kunci, susunBadanTugas(isian));
}
